import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Check, ArrowRight, Loader2, AlertTriangle, Zap, Heart, GraduationCap, Globe2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fileToImage, validateSelfie, loadFaceModels, MODEL_VERSION, GEOMETRY_VERSION } from "@/lib/face";
import { LogoBadge } from "@/components/Header";
import { Sticker } from "@/components/Stickers";
import InstitutionAutocomplete from "@/components/InstitutionAutocomplete";

const STEPS = ["Selfie", "Age", "About you", "Match mode"];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // selfie state
  const [previewUrl, setPreviewUrl] = useState("");
  const [photoData, setPhotoData] = useState(""); // dataURL stored alongside
  const [embedding, setEmbedding] = useState([]);
  const [geometry, setGeometry] = useState(null);
  const [quality, setQuality] = useState(0);
  const [faceStatus, setFaceStatus] = useState(""); // "loading" | "ok" | "no_face"
  const fileRef = useRef();

  // profile state
  const [age, setAge] = useState(20);
  const [over18, setOver18] = useState(false);
  const [gender, setGender] = useState("");
  const [lookingFor, setLookingFor] = useState("");
  const [mode, setMode] = useState("doppel");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [institution, setInstitution] = useState(null); // {id,name,country,city,type,source} | null
  const [discoverScope, setDiscoverScope] = useState("global"); // "global" | "institution"
  // Match-with age range. Initialized once `age` is touched so it starts
  // sensibly centred on the user's age (±5 / +10) until they adjust.
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(30);
  const [rangeTouched, setRangeTouched] = useState(false);

  useEffect(() => {
    loadFaceModels().catch(() => {});
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setFaceStatus("loading");
    try {
      const { img } = await fileToImage(file);
      await new Promise((r) => (img.complete ? r() : (img.onload = r)));

      // 1) Local face validation — surfaces a specific, helpful reason.
      let v;
      try {
        v = await validateSelfie(img);
      } catch (err) {
        setFaceStatus("no_face");
        setError("We couldn't read that photo. Try a JPG or PNG under 8MB.");
        return;
      }
      if (!v.ok) {
        setFaceStatus("no_face");
        setError(v.message);
        return;
      }

      // 2) Upload to object storage — moderation may still reject (e.g. NSFW).
      try {
        const form = new FormData();
        form.append("file", file, file.name || "selfie.jpg");
        const { data } = await api.post("/upload/selfie", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const absolute = data.url.startsWith("http")
          ? data.url
          : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setPhotoData(absolute);
        setPreviewUrl(absolute);
        setEmbedding(v.embedding);
        setGeometry(v.geometry || null);
        setQuality(v.quality_score);
        setFaceStatus("ok");
      } catch (uploadErr) {
        setFaceStatus("no_face");
        // Use the backend's specific message (e.g. moderation reason) when available.
        const detail = formatApiError(uploadErr);
        setError(detail || "Upload failed. Check your connection and try again.");
      }
    } catch (e) {
      setFaceStatus("no_face");
      setError("Something went wrong. Try a different photo.");
    }
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const finish = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding", {
        age,
        gender,
        looking_for: lookingFor,
        mode,
        bio,
        location,
        photo_url: photoData || (user?.photo_url ?? null),
        embedding,
        quality_score: quality || 0.6,
        model_version: MODEL_VERSION,
        geometry: geometry || undefined,
        geometry_version: geometry ? GEOMETRY_VERSION : undefined,
        age_min: ageMin,
        age_max: ageMax,
        institution: institution || undefined,
        // If they picked an institution, default discover to the campus feed.
        discover_scope: institution ? discoverScope : "global",
      });
      setUser(data);
      // Priority: explicit twin room → stored return_to → default discover
      const pendingTwinRoom = localStorage.getItem("dc_pending_twin_room");
      const returnTo = localStorage.getItem("dc_return_to");
      if (pendingTwinRoom) {
        localStorage.removeItem("dc_pending_twin_room");
        navigate(`/compare/${pendingTwinRoom}`);
      } else if (returnTo) {
        localStorage.removeItem("dc_return_to");
        navigate(returnTo);
      } else {
        navigate("/discover");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const canContinue = (() => {
    if (step === 0) return faceStatus === "ok";
    if (step === 1) return over18 && age >= 18;
    if (step === 2) return gender && lookingFor;
    if (step === 3) return Boolean(mode);
    return false;
  })();

  return (
    <div className="crush-bg min-h-screen" data-testid="onboarding-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16">
        <LogoBadge />

        {/* progress */}
        <div className="mt-6 flex items-center gap-3">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1">
              <div className={`h-2 rounded-full ${i <= step ? "bg-gradient-to-r from-pink-500 to-orange-400" : "bg-slate-200"}`} />
              <div className={`mt-2 text-xs font-display font-bold ${i === step ? "text-pink-600" : "text-slate-400"}`}>{label}</div>
            </div>
          ))}
        </div>

        <div className="crush-frame mt-6 p-6 sm:p-10 relative">
          <Sticker kind="heart" size={56} className="absolute -top-6 -right-4 rotate-12 hidden sm:inline-flex" />

          {step === 0 && (
            <div data-testid="step-selfie">
              <h2 className="font-display text-4xl font-bold text-slate-900">Drop the selfie 📸</h2>
              <p className="mt-2 text-slate-600 font-body">We use it to find your DoppelCrush. One face, please.</p>

              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="selfie-file-input"
              />

              <div className="mt-6 flex flex-col sm:flex-row items-center gap-6">
                <div className="w-40 h-40 rounded-3xl overflow-hidden bg-pink-50 border-4 border-dashed border-pink-200 grid place-items-center">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Selfie preview" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-12 h-12 text-pink-400" />
                  )}
                </div>
                <div className="flex-1">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="crush-cta rounded-full font-display font-bold px-6 py-3 inline-flex items-center gap-2"
                    data-testid="selfie-upload-btn"
                  >
                    <Camera className="w-5 h-5" /> {previewUrl ? "Pick a different selfie" : "Upload selfie"}
                  </button>
                  <div className="mt-3 text-sm font-body">
                    {faceStatus === "loading" && (
                      <span className="text-slate-600 inline-flex items-center gap-2" data-testid="selfie-status-loading"><Loader2 className="w-4 h-4 animate-spin" /> Reading your face card…</span>
                    )}
                    {faceStatus === "ok" && (
                      <>
                        <span className="text-emerald-600 inline-flex items-center gap-2" data-testid="selfie-status-ok"><Check className="w-4 h-4" /> Face card captured.</span>
                        <div className="mt-2 flex items-center gap-2 text-xs">
                          <span className="text-slate-500">Quality</span>
                          <div className="w-32 h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-pink-500 to-orange-400"
                              style={{ width: `${Math.round(quality * 100)}%` }}
                            />
                          </div>
                          <span className="font-display font-bold text-slate-700">{Math.round(quality * 100)}%</span>
                        </div>
                      </>
                    )}
                    {faceStatus === "no_face" && (
                      <div
                        className="mt-2 rounded-2xl border-2 border-rose-200 bg-rose-50 p-3 text-rose-700"
                        data-testid="selfie-status-error"
                        role="alert"
                      >
                        <div className="inline-flex items-center gap-2 font-display font-bold">
                          <AlertTriangle className="w-4 h-4" /> {error || "Need a clearer single face."}
                        </div>
                        <div className="mt-2 text-xs text-rose-600 font-body">
                          Try again with a photo that matches the tips below.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Always-visible selfie tips so users know what works before they try. */}
              <div
                className="mt-6 rounded-3xl border-2 border-pink-100 bg-pink-50/50 p-5"
                data-testid="selfie-tips"
              >
                <div className="font-display font-bold text-slate-900 text-sm uppercase tracking-widest">
                  What works best
                </div>
                <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm font-body text-slate-700">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> Solo selfie — only your face in frame</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> Look straight at the camera</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> Good lighting — daylight wins</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> Face takes up ~⅓ of the photo</li>
                  <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" /> No sunglasses, masks or hats hiding your face</li>
                  <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" /> JPG, PNG or WebP · up to 8MB</li>
                </ul>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-age">
              <h2 className="font-display text-4xl font-bold text-slate-900">Are you 18+?</h2>
              <p className="mt-2 text-slate-600 font-body">DoppelCrush is 18 and over. We have to ask.</p>
              <div className="mt-6">
                <label className="block">
                  <span className="block text-sm font-display font-bold text-slate-700 mb-1">Your age</span>
                  <input
                    type="number"
                    min={18}
                    max={120}
                    value={age}
                    onChange={(e) => {
                      const v = parseInt(e.target.value || "0", 10);
                      setAge(v);
                      // Auto-centre the match range on the user's age until
                      // they touch the slider — keeps Profile/Onboarding in
                      // sync without a re-render side effect.
                      if (!rangeTouched && v >= 18 && v <= 120) {
                        setAgeMin(Math.max(18, v - 5));
                        setAgeMax(Math.min(80, v + 10));
                      }
                    }}
                    className="w-40 rounded-2xl border-2 border-slate-200 px-4 py-3 font-body focus:border-pink-400 outline-none"
                    data-testid="age-input"
                  />
                </label>
                <label className="mt-5 flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={over18} onChange={(e) => setOver18(e.target.checked)} className="w-5 h-5 accent-pink-500" data-testid="age-confirm-checkbox" />
                  <span className="font-body text-slate-700">I confirm I&apos;m 18 years or older.</span>
                </label>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-about">
              <h2 className="font-display text-4xl font-bold text-slate-900">About you</h2>
              <p className="mt-2 text-slate-600 font-body">Tell us your vibe and who you want to see.</p>

              <div className="mt-6 grid md:grid-cols-2 gap-6">
                <div>
                  <div className="text-sm font-display font-bold text-slate-700 mb-2">I am a…</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ["woman", "Woman"],
                      ["man", "Man"],
                      ["nonbinary", "Nonbinary"],
                    ].map(([k, l]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setGender(k)}
                        className={`rounded-full px-4 py-2 font-display font-bold border-2 ${gender === k ? "crush-cta border-transparent" : "bg-white border-slate-200 text-slate-700"}`}
                        data-testid={`gender-${k}`}
                      >{l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-display font-bold text-slate-700 mb-2">Show me…</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ["women", "Women"],
                      ["men", "Men"],
                      ["everyone", "Everyone"],
                    ].map(([k, l]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setLookingFor(k)}
                        className={`rounded-full px-4 py-2 font-display font-bold border-2 ${lookingFor === k ? "crush-cta border-transparent" : "bg-white border-slate-200 text-slate-700"}`}
                        data-testid={`looking-${k}`}
                      >{l}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Match-with age range */}
              <div className="mt-6">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-sm font-display font-bold text-slate-700">Match me with ages</span>
                  <span className="font-display font-bold text-pink-600 text-sm" data-testid="age-range-readout">
                    {ageMin} – {ageMax}{ageMax >= 80 ? "+" : ""}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[11px] font-body text-slate-500 uppercase tracking-wide">Min</span>
                    <input
                      type="range"
                      min={18}
                      max={80}
                      value={Math.min(ageMin, ageMax)}
                      onChange={(e) => {
                        const v = Math.min(Number(e.target.value), ageMax);
                        setAgeMin(v);
                        setRangeTouched(true);
                      }}
                      className="w-full accent-pink-500"
                      data-testid="age-min-slider"
                      aria-label="Minimum match age"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] font-body text-slate-500 uppercase tracking-wide">Max</span>
                    <input
                      type="range"
                      min={18}
                      max={80}
                      value={Math.max(ageMin, ageMax)}
                      onChange={(e) => {
                        const v = Math.max(Number(e.target.value), ageMin);
                        setAgeMax(v);
                        setRangeTouched(true);
                      }}
                      className="w-full accent-pink-500"
                      data-testid="age-max-slider"
                      aria-label="Maximum match age"
                    />
                  </label>
                </div>
              </div>

              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-display font-bold text-slate-700 mb-1">Location (optional)</span>
                  <input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Brooklyn"
                    className="w-full rounded-2xl border-2 border-slate-200 px-4 py-3 font-body focus:border-pink-400 outline-none"
                    data-testid="location-input"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-display font-bold text-slate-700 mb-1">Bio (optional)</span>
                  <input
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    maxLength={80}
                    placeholder="Late-reply queen with strong opinions on pasta."
                    className="w-full rounded-2xl border-2 border-slate-200 px-4 py-3 font-body focus:border-pink-400 outline-none"
                    data-testid="bio-input"
                  />
                </label>
              </div>

              {/* Optional institution — campus filter for matching */}
              <div className="mt-6">
                <label className="block">
                  <span className="block text-sm font-display font-bold text-slate-700 mb-1 inline-flex items-center gap-1.5">
                    <GraduationCap className="w-4 h-4 text-pink-600" /> School, college or uni
                  </span>
                  <span className="block text-xs font-body text-slate-500 mb-2">
                    Optional — add yours to match on your campus, or stay global anytime.
                  </span>
                  <InstitutionAutocomplete
                    value={institution}
                    onChange={(inst) => {
                      setInstitution(inst);
                      // Auto-flip the scope based on intent — if they cleared
                      // the field, drop back to global.
                      if (!inst) setDiscoverScope("global");
                      else if (discoverScope === "global") setDiscoverScope("institution");
                    }}
                  />
                </label>
                {institution ? (
                  <div className="mt-3 inline-flex p-1 bg-white border-2 border-slate-100 rounded-full" data-testid="discover-scope-toggle">
                    <button
                      type="button"
                      onClick={() => setDiscoverScope("institution")}
                      className={`px-4 py-1.5 rounded-full text-xs font-display font-bold inline-flex items-center gap-1.5 transition-colors ${discoverScope === "institution" ? "bg-slate-900 text-white" : "text-slate-600"}`}
                      data-testid="scope-campus-btn"
                    >
                      <GraduationCap className="w-3 h-3" /> My campus
                    </button>
                    <button
                      type="button"
                      onClick={() => setDiscoverScope("global")}
                      className={`px-4 py-1.5 rounded-full text-xs font-display font-bold inline-flex items-center gap-1.5 transition-colors ${discoverScope === "global" ? "bg-slate-900 text-white" : "text-slate-600"}`}
                      data-testid="scope-global-btn"
                    >
                      <Globe2 className="w-3 h-3" /> Global
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-mode">
              <h2 className="font-display text-4xl font-bold text-slate-900">Pick your mode</h2>
              <p className="mt-2 text-slate-600 font-body">You can switch any time.</p>
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setMode("doppel")}
                  className={`text-left p-6 rounded-3xl border-4 ${mode === "doppel" ? "border-pink-500 bg-pink-50" : "border-white bg-white"} shadow-md`}
                  data-testid="mode-doppel"
                >
                  <div className="flex items-center gap-2 text-pink-600"><Heart className="w-5 h-5" /><span className="font-display font-bold">Doppel Mode</span></div>
                  <div className="font-display text-2xl font-bold mt-1">Twin energy.</div>
                  <div className="text-slate-600 font-body mt-1">People who match your face card.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("chaos")}
                  className={`text-left p-6 rounded-3xl border-4 ${mode === "chaos" ? "border-orange-500 bg-orange-50" : "border-white bg-white"} shadow-md`}
                  data-testid="mode-chaos"
                >
                  <div className="flex items-center gap-2 text-orange-600"><Zap className="w-5 h-5" /><span className="font-display font-bold">Chaos Mode</span></div>
                  <div className="font-display text-2xl font-bold mt-1">Plot twist.</div>
                  <div className="text-slate-600 font-body mt-1">A total switch-up from your usual type.</div>
                </button>
              </div>
            </div>
          )}

          {error ? <div className="mt-4 text-sm text-rose-600 font-body" data-testid="onboarding-error">{error}</div> : null}

          <div className="mt-8 flex items-center justify-between">
            <button
              type="button"
              onClick={back}
              disabled={step === 0}
              className="crush-secondary rounded-full px-5 py-2.5 font-display font-bold disabled:opacity-40"
              data-testid="onboarding-back-btn"
            >Back</button>
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={next}
                disabled={!canContinue}
                className="crush-cta rounded-full px-6 py-3 font-display font-bold inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="onboarding-next-btn"
              >Next <ArrowRight className="w-5 h-5" /></button>
            ) : (
              <button
                type="button"
                onClick={finish}
                disabled={!canContinue || busy}
                className="crush-cta rounded-full px-6 py-3 font-display font-bold inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="onboarding-finish-btn"
              >{busy ? "Loading…" : "Find my matches"} <ArrowRight className="w-5 h-5" /></button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
